import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../AuthContext';
import { Zap, ChevronRight, Eye, EyeOff, Building, Dumbbell, User, ArrowRight, ArrowLeft, Trophy, Award, Activity } from 'lucide-react';

/**
 * RegisterPage — Performance Club dark theme.
 *
 * Visual language matches /, /free, /about, /guide, /features, /contact:
 * near-black background, lime-green accents, layout-shell + editorial-*
 * utility classes. Single-page flow:
 *   1) Role chooser (3 cards: Gym Owner / Trainer / Client)
 *   2) Form (name / email / password / optional org id)
 *   3) For "I'm launching a new gym" → redirect to /contact with intent
 *      params (gym_owner inquiry), mirroring the founder-original flow.
 */
export const RegisterPage = () => {
  const { t } = useTranslation();
  const { register, error } = useAuth();
  const navigate = useNavigate();
  // Stage of the flow. 'gym_owner' immediately redirects to /contact,
  // so the only value that reaches the form is 'member' (Trainer or Client).
  const [signupMode, setSignupMode] = useState(null); // null | 'member' | 'gym_owner'
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'client', org_id: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Must match backend config.TERMS_VERSION (backend/routers/auth.py + backend/config.py).
  // The BE rejects the request with HTTP 400 if the version is stale, so the user
  // sees a clear "please re-read and accept v<X>" message. We hardcode here to
  // avoid a roundtrip on every page load; if the version ever drifts, the worst
  // case is the user gets the friendly "version is no longer current" error.
  const TERMS_VERSION = '2026-06-01.v1';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) return;
    setLoading(true);
    try {
      const data = { ...form };
      if (form.role === 'admin') delete data.org_id;
      // Stamp legal-acceptance audit trail on the registration request.
      data.terms_accepted_at = new Date().toISOString();
      data.terms_version = TERMS_VERSION;
      await register(data);
      navigate('/dashboard');
    } catch (err) {
      // Error handled in context
    } finally {
      setLoading(false);
    }
  };

  // The role chooser offers three cards. 'Gym Owner' redirects to the
  // /contact inquiry flow (gym_owner launch) — the form below is
  // reserved for Trainer and Client onboarding.
  const chooserRoles = [
    { value: 'gym_owner', icon: Building, label: t('registerPage.chooser.owner', { defaultValue: 'Gym Owner' }), desc: t('registerPage.chooser.ownerDesc', { defaultValue: 'Launch your own gym' }) },
    { value: 'trainer', icon: Dumbbell, label: t('registerPage.chooser.trainer', { defaultValue: 'Trainer' }), desc: t('registerPage.chooser.trainerDesc', { defaultValue: 'Coach members inside an existing gym' }) },
    { value: 'client', icon: User, label: t('registerPage.chooser.client', { defaultValue: 'Client' }), desc: t('registerPage.chooser.clientDesc', { defaultValue: 'Track sessions, points, and progress' }) },
  ];

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side — Performance Club hero */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden p-12 flex-col justify-between border-r border-[var(--color-border)]" style={{ background: 'linear-gradient(135deg, #060b0a 0%, #0d1512 48%, #09100e 100%)' }}>
        <div className="hero-media-plane" />
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-primary-200/30 bg-primary-200/10 font-display text-lg uppercase text-primary-100">GG</span>
            <div>
              <p className="font-display text-lg uppercase tracking-[0.18em] text-[var(--color-text)]">GymGamify</p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)]">Performance Club OS</p>
            </div>
          </Link>
        </div>

        <div className="relative z-10 space-y-6">
          <span className="hero-kicker">{t('registerPage.hero.kicker', { defaultValue: 'Membership onboarding' })}</span>
          <h2 className="editorial-title text-[var(--color-text)]">
            {t('registerPage.hero.title', { defaultValue: 'Join the Performance Club.' })}
          </h2>
          <p className="max-w-md text-lg leading-8 text-[var(--color-text-muted)]">
            {t('registerPage.hero.subtitle', {
              defaultValue:
                'One operating system for every gym: members earn, trainers coach, owners run the floor. Free forever, for every gym.',
            })}
          </p>
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            <div className="editorial-panel flex flex-col gap-1 p-3">
              <Trophy className="h-5 w-5 text-primary-100" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">{t('registerPage.hero.stat1Label', { defaultValue: 'Streaks' })}</span>
              <span className="font-display text-2xl text-[var(--color-text)]">{t('registerPage.hero.stat1Value', { defaultValue: 'Weekly' })}</span>
            </div>
            <div className="editorial-panel flex flex-col gap-1 p-3">
              <Award className="h-5 w-5 text-primary-100" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">{t('registerPage.hero.stat2Label', { defaultValue: 'Badges' })}</span>
              <span className="font-display text-2xl text-[var(--color-text)]">{t('registerPage.hero.stat2Value', { defaultValue: 'Configurable' })}</span>
            </div>
            <div className="editorial-panel flex flex-col gap-1 p-3">
              <Activity className="h-5 w-5 text-primary-100" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">{t('registerPage.hero.stat3Label', { defaultValue: 'Live' })}</span>
              <span className="font-display text-2xl text-[var(--color-text)]">{t('registerPage.hero.stat3Value', { defaultValue: '24 / 7' })}</span>
            </div>
          </div>
        </div>

        <div className="relative z-10">
          <p className="text-[10px] uppercase tracking-[0.22em] text-[var(--color-text-muted)]">
            © 2026 GymGamify. Powered by Polygon Media.
          </p>
        </div>
      </div>

      {/* Right side — form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12" style={{ background: 'var(--color-surface)' }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <Link to="/" className="lg:hidden flex items-center gap-3 justify-center mb-8">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary-200/30 bg-primary-200/10 font-display text-lg uppercase text-primary-100">GG</span>
            <p className="font-display text-lg uppercase tracking-[0.18em] text-[var(--color-text)]">GymGamify</p>
          </Link>

          {signupMode === null && (
            <div data-testid="register-role-chooser" className="space-y-6">
              <div>
                <span className="hero-kicker">{t('registerPage.roleChooser.kicker', { defaultValue: 'Step 1 of 2' })}</span>
                <h2 className="mt-3 editorial-title text-3xl text-[var(--color-text)]">
                  {t('registerPage.roleChooser.title', { defaultValue: 'What brings you here?' })}
                </h2>
                <p className="mt-2 text-sm text-[var(--color-text-muted)]">
                  {t('registerPage.roleChooser.subtitle', { defaultValue: 'Pick the path that fits.' })}
                </p>
              </div>
              <div className="space-y-3">
                {chooserRoles.map((role) => {
                  const Icon = role.icon;
                  return (
                    <button
                      key={role.value}
                      type="button"
                      onClick={() => {
                        if (role.value === 'gym_owner') {
                          navigate('/contact?source=register&intent=gym_owner');
                          return;
                        }
                        setSignupMode('member');
                        setForm((prev) => ({ ...prev, role: role.value }));
                      }}
                      className="w-full text-left p-4 rounded-2xl border border-[var(--color-border)] bg-white/[0.03] hover:border-primary-200/40 hover:bg-white/[0.06] transition-all flex items-center gap-4 group"
                      data-testid={`register-chooser-${role.value}`}
                    >
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary-200/15 bg-primary-200/10">
                        <Icon className="w-5 h-5 text-primary-100" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base font-semibold text-[var(--color-text)]">{role.label}</p>
                        <p className="text-xs text-[var(--color-text-muted)] mt-0.5">{role.desc}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-[var(--color-text-muted)] group-hover:text-[var(--color-text)]" />
                    </button>
                  );
                })}
              </div>
              <div className="pt-6 border-t border-[var(--color-border)]">
                <p className="text-center text-sm text-[var(--color-text-muted)]">
                  {t('registerPage.signInPrompt', { defaultValue: 'Already have an account?' })}{' '}
                  <Link to="/login" className="text-primary-100 font-semibold hover:text-primary-200">
                    {t('registerPage.signInLink', { defaultValue: 'Sign in' })}
                  </Link>
                </p>
              </div>
            </div>
          )}

          {signupMode === 'member' && (
            <div data-testid="register-member-form" className="space-y-5">
              <button
                type="button"
                onClick={() => setSignupMode(null)}
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                <ArrowLeft className="w-3 h-3" />
                {t('registerPage.backToChooser', { defaultValue: 'Back' })}
              </button>
              <div>
                <span className="hero-kicker">{t('registerPage.form.kicker', { defaultValue: 'Step 2 of 2' })}</span>
                <h2 className="mt-3 editorial-title text-3xl text-[var(--color-text)]">
                  {t('registerPage.form.title', { defaultValue: 'Create your account.' })}
                </h2>
                <p className="mt-2 text-sm text-[var(--color-text-muted)]">
                  {t('registerPage.form.subtitle', { defaultValue: 'You\u2019re signing up as ' })}
                  <strong className="text-[var(--color-text)]">
                    {form.role === 'trainer' ? t('registerPage.chooser.trainer', { defaultValue: 'Trainer' }) : t('registerPage.chooser.client', { defaultValue: 'Client' })}
                  </strong>.
                </p>
              </div>

              {error && (
                <div className="border border-red-500/30 bg-red-500/10 text-red-200 p-3 rounded-xl text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] mb-2">
                    {t('registerPage.form.nameLabel', { defaultValue: 'Full name' })}
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all"
                    placeholder={t('registerPage.form.namePlaceholder', { defaultValue: 'John Doe' })}
                    required
                    data-testid="register-name"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] mb-2">
                    {t('registerPage.form.emailLabel', { defaultValue: 'Email' })}
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all"
                    placeholder={t('registerPage.form.emailPlaceholder', { defaultValue: 'you@example.com' })}
                    required
                    data-testid="register-email"
                  />
                </div>

                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] mb-2">
                    {t('registerPage.form.passwordLabel', { defaultValue: 'Password' })}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className="w-full px-4 py-3 pr-12 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all"
                      placeholder={t('registerPage.form.passwordPlaceholder', { defaultValue: 'At least 6 characters' })}
                      required
                      minLength={6}
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {form.role === 'client' && (
                  <div>
                    <label className="block text-[10px] font-semibold uppercase tracking-[0.22em] text-[var(--color-text-muted)] mb-2">
                      {t('registerPage.form.orgLabel', { defaultValue: 'Organization ID' })}
                    </label>
                    <input
                      type="text"
                      value={form.org_id}
                      onChange={(e) => setForm({ ...form, org_id: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)]/60 focus:border-primary-200/40 focus:bg-white/[0.06] focus:outline-none focus:ring-2 focus:ring-primary-200/20 transition-all"
                      placeholder={t('registerPage.form.orgPlaceholder', { defaultValue: 'Ask your gym admin for this' })}
                      required
                      data-testid="register-org"
                    />
                    <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
                      {t('registerPage.form.orgHelp', { defaultValue: 'Trainers don\u2019t need an org id — switch role above.' })}
                    </p>
                  </div>
                )}

                <label className="flex items-start gap-3 cursor-pointer pt-1">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-[var(--color-border)] bg-white/[0.04] text-primary-500 focus:ring-2 focus:ring-primary-200/20 cursor-pointer"
                    data-testid="register-terms"
                  />
                  <span className="text-xs leading-5 text-[var(--color-text-muted)]">
                    {t('registerPage.form.termsLabelStart', { defaultValue: 'I agree to the ' })}
                    <Link
                      to="/legal/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-100 hover:text-primary-200 underline"
                    >
                      {t('registerPage.form.termsLink', { defaultValue: 'Terms of Service' })}
                    </Link>
                    {t('registerPage.form.termsLabelMid', { defaultValue: ' and acknowledge the ' })}
                    <Link
                      to="/legal/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-100 hover:text-primary-200 underline"
                    >
                      {t('registerPage.form.privacyLink', { defaultValue: 'Privacy Policy' })}
                    </Link>
                    .
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading || !acceptedTerms}
                  className="w-full bg-gradient-to-r from-primary-500 to-primary-300 text-surface font-semibold py-3.5 rounded-xl hover:from-primary-300 hover:to-primary-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-primary-500/20"
                  data-testid="register-submit"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-surface border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      {t('registerPage.form.submit', { defaultValue: 'Create account' })}
                      <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>

              <div className="pt-4 border-t border-[var(--color-border)]">
                <p className="text-center text-sm text-[var(--color-text-muted)]">
                  {t('registerPage.signInPrompt', { defaultValue: 'Already have an account?' })}{' '}
                  <Link to="/login" className="text-primary-100 font-semibold hover:text-primary-200">
                    {t('registerPage.signInLink', { defaultValue: 'Sign in' })}
                  </Link>
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
